import type { DashboardExtra } from '@qlk/shared';
import { Link } from 'react-router-dom';

import { dinhDangTien, dinhDangTienGon, Money, Skeleton } from '../../../ui/index.js';
import { huongDelta, phanTram } from './dinhDang.js';
import {
  buocTheoKhoa,
  dungChuoiDongTien,
  phanTramGon,
  type BuocTien,
  type ChuoiDongTien,
} from './dongTien.js';

/**
 * Khối "Dòng tiền" của trang Tổng quan — **một** thẻ, hai cột.
 *
 * ## Vì sao gộp bảy ô rời thành một khối
 *
 * Bản trước bày bảy ô ngang hàng trong `StatStrip`: Công ty thực nhận, Phần
 * chia Network, Lượt xem, Subscribers, Kênh kiếm tiền, Chia nhân sự, Công ty
 * giữ lại. Bốn trong bảy ô là **bốn chặng của cùng một phép trừ**, nhưng xếp
 * ngang hàng thì chúng đọc ra là bốn số độc lập — và hai ô "Công ty thực nhận"
 * / "Công ty giữ lại" gần nhau về giá trị nên trông như một ô bị lặp. Không ô
 * nào nói được điều người ta mở trang để biết: *mỗi đồng doanh thu gộp đi đâu*.
 *
 * Nay: cột trái một con số chính (ai thấy gì do máy chủ quyết), cột phải cả
 * chuỗi — một thanh xếp chồng theo tỷ trọng, rồi danh sách bước đọc từ trên
 * xuống đúng thứ tự trừ.
 *
 * ## Hai cột, xếp chồng dưới 720px
 *
 * Đo bằng `@container/dong-tien` chứ không bằng khung nhìn: khối nằm trong vùng
 * nội dung, mà vùng nội dung hẹp hơn khung nhìn đúng bằng bề ngang thanh bên
 * (xem `docs/ui.md` mục 9.2). Trên laptop 1280px vùng nội dung chỉ 992px, hai
 * cột còn ~470px mỗi cột — vẫn đủ cho một con số tiền và một danh sách bước.
 */

/** Màu chữ của giá trị theo vai của bước. */
const MAU_GIA_TRI: Record<BuocTien['vai'], string> = {
  tru: 'text-chu',
  moc: 'text-chu',
  cuoi: 'text-nhan',
};

export interface KhoiDongTienProps {
  chuoi: DashboardExtra['chuoiTien'] | undefined;
  chia: NonNullable<DashboardExtra['chiaNhanSu']> | null | undefined;
  /** Nhãn kỳ đang xem: `28 ngày qua`. */
  nhanKy: string;
  /** Tăng/giảm **doanh thu gộp** so kỳ trước; `null` = không có kỳ trước để so. */
  deltaGop: number | null;
  /**
   * Nhãn của phép so: `so kỳ trước`, hoặc `so 01/09 – 26/09/2026` khi kỳ chưa
   * trọn (xem `lib/kyTrang.nhanSoKyTruoc`). Trang truyền xuống chứ không tự
   * ghép ở đây — kỳ là việc của trang.
   */
  nhanSoSanh: string;
  /** Số kênh người đang xem quản lý — dòng phụ của bản người xem hẹp. */
  soKenh: number | undefined;
  /** Tỷ lệ của người đang xem, để ghi "Phần của bạn · 7%". */
  pctCuaToi?: number | null;
  dangTai: boolean;
  loi?: string | null;
}

/** Một dòng trong danh sách bước. */
function DongBuoc({ b, currency }: { b: BuocTien; currency: string }) {
  const nhanManh = b.vai !== 'tru';

  return (
    <li
      data-buoc={b.khoa}
      className={`flex items-center gap-2.5 py-1.5 ${
        nhanManh ? '-mx-2 rounded-o px-2' : ''
      } ${b.vai === 'moc' ? 'bg-neutral-nen' : ''} ${b.vai === 'cuoi' ? 'bg-nhan-nen' : ''}`}
    >
      {/*
        Chấm màu **chỉ** cho dòng có đoạn trên thanh: dòng mốc không có đoạn
        nào để chỉ tới, một chấm ở đó là một lời hứa sai.
      */}
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full border border-vien"
        style={b.veDoan ? { backgroundColor: b.mau, borderColor: b.mau } : undefined}
      />

      <span className="flex min-w-0 flex-1 flex-col @min-[420px]/buoc:flex-row @min-[420px]/buoc:items-baseline @min-[420px]/buoc:gap-2">
        <span className={`truncate text-co-ban text-chu ${nhanManh ? 'font-bold' : 'font-medium'}`}>
          {b.nhan}
        </span>
        {b.ghiChu !== undefined && (
          <span className="truncate text-phu text-chu-phu">{b.ghiChu}</span>
        )}
      </span>

      <span className="so-lieu w-14 shrink-0 text-right text-phu text-chu-phu">
        {phanTramGon(b.pct)}
      </span>
      <span
        data-tien=""
        className={`so-lieu w-24 shrink-0 text-right text-bang font-bold ${MAU_GIA_TRI[b.vai]} ${
          b.giaTri < 0 ? 'text-giam' : ''
        }`}
      >
        {dinhDangTien(b.giaTri, currency === 'VND' ? 'VND' : 'USD')}
      </span>
    </li>
  );
}

/** Thanh xếp chồng: bề rộng mỗi đoạn tỉ lệ với số tiền trên gộp. */
function Thanh({ c }: { c: ChuoiDongTien }) {
  return (
    <div
      role="img"
      aria-label={c.nhanThanh}
      data-khoi="thanh-dong-tien"
      className="flex h-2.5 w-full overflow-hidden rounded-full bg-neutral-nen"
    >
      {c.doan.map((d) => (
        <span
          key={d.khoa}
          data-doan={d.khoa}
          title={`${d.nhan} · ${phanTramGon(d.pct)}`}
          /* `minWidth` 2px: một khoản 0,1% vẫn phải thấy được là có, nhưng
             không được chiếm chỗ của khoản lớn — nên đặt ở `min`, không ở
             `width`. */
          style={{ width: `${String(d.pct)}%`, minWidth: '2px', backgroundColor: d.mau }}
        />
      ))}
    </div>
  );
}

export function KhoiDongTien({
  chuoi,
  chia,
  nhanKy,
  deltaGop,
  nhanSoSanh,
  soKenh,
  pctCuaToi,
  dangTai,
  loi,
}: KhoiDongTienProps) {
  const c = dungChuoiDongTien({
    chuoi,
    chia,
    ...(pctCuaToi === undefined ? {} : { pctCuaToi }),
  });
  const donVi = c.currency === 'VND' ? 'VND' : 'USD';
  const xemDayDu = c.che === 'day-du';

  /*
   * Con số chính = **dòng kết của chuỗi**, không phải một ô chọn riêng: người
   * xem đủ thấy "Công ty giữ lại", người xem hẹp thấy phần của chính họ, và khi
   * module Nhân sự không trả phần chia thì chuỗi dừng ở "Thực nhận". Cả ba đều
   * là dòng `vai: 'cuoi'` nên không có chỗ nào để hai luật lệch nhau.
   */
  const soChinh = c.buoc.find((b) => b.vai === 'cuoi');
  const thucNhan = buocTheoKhoa(c, 'thucNhan');

  /** Nhãn của con số chính — giữ đúng thuật ngữ đã chốt (docs/ui.md mục 16). */
  const nhanSoChinh =
    c.che === 'day-du'
      ? 'Công ty giữ lại'
      : c.che === 'hep'
        ? 'Thu nhập của bạn'
        : 'Công ty thực nhận';

  return (
    <section
      data-khoi="dong-tien"
      aria-label={`Dòng tiền · ${nhanKy}`}
      className="@container/dong-tien rounded-the border border-vien bg-be-mat"
    >
      {loi !== null && loi !== undefined ? (
        <p className="p-6 text-co-ban text-bad-chu">Không tải được dòng tiền: {loi}</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 p-6 @min-[720px]/dong-tien:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
          {/* ------------------------- Cột trái ------------------------- */}
          <div className="min-w-0">
            <p className="text-phu font-medium text-chu-phu">
              {nhanSoChinh} · {nhanKy}
            </p>

            {/*
              Cỡ 44px là **cỡ riêng của khối này**, không có trong thang token:
              nó là con số duy nhất của trang được phép to hơn tiêu đề trang.
              Dưới 720px bề ngang khối thì về 32px — một con số tiền bảy chữ số
              ở 44px cần ~260px, mà khổ 390 chỉ còn ~294px kể cả padding.
            */}
            <p className="so-lieu mt-1 text-[32px] leading-[1.1] font-bold text-nhan @min-[720px]/dong-tien:text-[44px]">
              {dangTai ? '…' : c.coSoLieu && soChinh ? dinhDangTien(soChinh.giaTri, donVi) : '—'}
            </p>

            {!c.coSoLieu ? (
              <p className="mt-2 text-phu text-chu-phu">Không có số liệu trong kỳ này</p>
            ) : c.che === 'chi-thuc-nhan' ? (
              <p className="mt-2 text-co-ban text-chu-phu">
                <span className="so-lieu">{phanTramGon(soChinh?.pct ?? 0)}</span> doanh thu gộp, sau
                thuế Mỹ, Network và VAT — chưa có phần chia nhân sự của kỳ này
              </p>
            ) : xemDayDu ? (
              <p className="mt-2 text-co-ban text-chu-phu">
                giữ <span className="so-lieu">{phanTramGon(soChinh?.pct ?? 0)}</span> doanh thu gộp,
                sau Network, thuế, chi phí và % nhân sự
              </p>
            ) : (
              <p className="mt-2 text-co-ban text-chu-phu">
                {pctCuaToi === null || pctCuaToi === undefined ? (
                  'phần của bạn trong lợi nhuận kênh'
                ) : (
                  <>
                    <span className="so-lieu">{phanTramGon(pctCuaToi)}</span> lợi nhuận kênh
                  </>
                )}
                {soKenh !== undefined && ` của ${String(soKenh)} kênh bạn quản lý`}
              </p>
            )}

            {/* Chip: thực nhận + so kỳ trước + tạm tính. */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {c.coSoLieu && thucNhan && c.che !== 'chi-thuc-nhan' && (
                <span className="inline-flex items-center gap-1.5 rounded-chip bg-neutral-nen px-2 py-1 text-phu font-medium text-neutral-chu">
                  Thực nhận <Money value={thucNhan.giaTri} currency={donVi} />
                </span>
              )}

              {/*
                Mũi tên đo **doanh thu gộp**, không đo con số bên trên: `/summary`
                chỉ có `grossDeltaPercent`. Ghi rõ "doanh thu gộp" trong chip —
                dán một mũi tên cạnh "Công ty giữ lại" rồi để người đọc tưởng nó
                nói về con số đó là tệ hơn không có mũi tên nào.
              */}
              {c.coSoLieu && deltaGop !== null && (
                <span
                  className={`inline-flex items-center gap-1 rounded-chip bg-neutral-nen px-2 py-1 text-phu font-semibold ${
                    huongDelta(deltaGop) === 'tang'
                      ? 'text-tang'
                      : huongDelta(deltaGop) === 'giam'
                        ? 'text-giam'
                        : 'text-chu-phu'
                  }`}
                >
                  {huongDelta(deltaGop) === 'tang'
                    ? '▲'
                    : huongDelta(deltaGop) === 'giam'
                      ? '▼'
                      : '—'}{' '}
                  {phanTram(deltaGop)} doanh thu gộp {nhanSoSanh}
                </span>
              )}

              {/* Tháng đã chốt đủ thì **bỏ** chip (docs/ui.md mục 16). */}
              {chia?.tamTinh === true && (
                <span className="inline-flex items-center rounded-chip bg-warn-nen px-2 py-1 text-phu font-medium text-warn-chu">
                  tạm tính{xemDayDu ? '' : ' · chốt khi đối soát tháng'}
                </span>
              )}
            </div>

            {c.che === 'hep' && (
              <Link
                to="/thu-nhap-cua-toi"
                className="mt-3 inline-block text-phu font-semibold text-nhan hover:text-nhan-dam"
              >
                Xem Thu nhập của tôi →
              </Link>
            )}
          </div>

          {/* ------------------------- Cột phải ------------------------- */}
          <div className="@container/buoc min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-tieu-de-khoi font-semibold text-chu">Doanh thu gộp đi về đâu</h2>
              <p className="text-phu text-chu-phu">
                gộp <span className="so-lieu font-semibold">{dinhDangTienGon(c.gross)}</span>
              </p>
            </div>

            <div className="mt-3">
              {dangTai ? <Skeleton cao="10px" bo="tron" /> : <Thanh c={c} />}
            </div>

            {/* Kỳ rỗng: thanh trống ở trên đã nói hết, và câu "Không có số liệu
                trong kỳ này" đã nằm ở cột trái — in nó lần thứ hai trong cùng
                một thẻ chỉ làm người đọc tưởng đó là hai thông báo khác nhau. */}
            {c.coSoLieu && (
              <ol className="mt-3 flex flex-col">
                {c.buoc.map((b) => (
                  <DongBuoc key={b.khoa} b={b} currency={c.currency} />
                ))}
              </ol>
            )}

            {/*
              Chênh lệch chỉ xảy ra khi phần chia của kỳ lấy từ ảnh chụp lúc chốt
              mà doanh thu nguồn đã sửa sau đó. Nói ra, chứ không kéo một đoạn
              cho khít thanh.
            */}
            {c.coSoLieu && Math.abs(c.lech) >= 0.01 && (
              <p className="mt-2 text-phu text-warn-chu">
                Các bước lệch <span className="so-lieu">{dinhDangTien(c.lech, donVi)}</span> so với
                doanh thu gộp — phần chia của kỳ này lấy từ ảnh chụp lúc chốt.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
